import {
  ChakraMenu,
  ChakraMenuButton,
  ChakraMenuItem,
  ChakraMenuList,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
} from '@equip.health/ui';
import { IconTextMenuButtonOption } from '~/components/common/IconTextMenuButtonOption';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import { useFlags } from 'launchdarkly-react-client-sdk';

const { shadows } = EquipUIFireflyV2Theme;

type Props = {
  buttonId: string;
  /**
   * Renders the trigger as a small secondary button with no chevron, per the M4 note
   * template design. The care plan tab uses the default presentation.
   */
  isCompact?: boolean;
  isDisabled: boolean;
  onLegacyEdProblemClick: () => void;
  onProblemTypeSelected: (problemTypeCode: ProblemTypeCode) => void;
  onQoLGoalClick: () => void;
};

export const AddToCarePlanMenu = ({
  buttonId,
  isCompact,
  isDisabled,
  onLegacyEdProblemClick,
  onProblemTypeSelected,
  onQoLGoalClick,
}: Props) => {
  const { carePlanM4MedPsychProblems: isMedPsychProblemsEnabled } =
    useFlags<LDFlags>();
  const { trackEvent } = useAnalytics();

  return (
    <ChakraMenu>
      <ChakraMenuButton
        as={FireflyButton}
        id={buttonId}
        isDisabled={isDisabled}
        leftIcon={FAIconName.plusRegular}
        onClick={() => trackEvent(CarePlanEvents.clickOnAddToCarePlanMenu)}
        rightIcon={isCompact ? undefined : FAIconName.chevronDownRegular}
        size={isCompact ? 'small' : undefined}
        variant={isCompact ? 'secondary' : undefined}
      >
        Add to care plan
      </ChakraMenuButton>
      <ChakraMenuList
        boxShadow={shadows.medium}
        padding="14px 10px"
        zIndex={101}
      >
        <ChakraMenuItem
          onClick={() => {
            if (isMedPsychProblemsEnabled) {
              onProblemTypeSelected(ProblemTypeCode.EATING_DISORDER);
            } else {
              onLegacyEdProblemClick();
            }
            trackEvent(CarePlanEvents.clickOnEdProblemMenuOption);
          }}
        >
          <IconTextMenuButtonOption
            iconName={FAIconName.forkKnifeRegular}
            title="ED problem"
          />
        </ChakraMenuItem>
        {isMedPsychProblemsEnabled && (
          <>
            <ChakraMenuItem
              onClick={() => {
                trackEvent(CarePlanEvents.clickOnPsychiatricProblemMenuOption);
                onProblemTypeSelected(ProblemTypeCode.PSYCHIATRIC);
              }}
            >
              <IconTextMenuButtonOption
                iconName={FAIconName.cloudBoltRegular}
                title="Psychiatric problem"
              />
            </ChakraMenuItem>
            <ChakraMenuItem
              onClick={() => {
                trackEvent(CarePlanEvents.clickOnMedicalProblemMenuOption);
                onProblemTypeSelected(ProblemTypeCode.MEDICAL);
              }}
            >
              <IconTextMenuButtonOption
                iconName={FAIconName.stethoscopeRegular}
                title="Medical problem"
              />
            </ChakraMenuItem>
          </>
        )}
        <ChakraMenuItem
          onClick={() => {
            onQoLGoalClick();
            trackEvent(CarePlanEvents.clickOnQualityOfLifeGoalMenuOption);
          }}
        >
          <IconTextMenuButtonOption
            iconName={FAIconName.penFieldRegular}
            title="Quality of life goal"
          />
        </ChakraMenuItem>
      </ChakraMenuList>
    </ChakraMenu>
  );
};
